import React from "react";

import ConferenceInviteModal from "~/components/conferenceInviteModal";
import CallNotificationToast from "~/components/callNotificationToast";
import type { IncomingCallToastPayload } from "~/components/callNotificationToast";
import CreateTaskModal, {
  type CreateTaskSubmission,
} from "~/components/createTaskModal";
import ResolveCallModal, {
  type ResolveCallSubmission,
} from "~/components/resolveCallModal";
import type { CallResolutionContext } from "~/context/call/useCallResolution";
import type {
  CallTakenNotice,
  ConferenceInvitePayload,
  SipCallState,
} from "~/services/calling/types";

type CallUiShellProps = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  pendingInvite?: ConferenceInvitePayload | null;
  callDirection?: "incoming" | "outgoing";
  callStatus: SipCallState;
  isOnDashboardRoute: boolean;
  incomingToastCalls: IncomingCallToastPayload[];
  callTakenNotices: CallTakenNotice[];
  answerLockoutActive?: boolean;
  answerLockoutMessage?: string;
  onDismissInvite: () => void;
  onJoinConferenceRoom: (room: string) => Promise<void>;
  onAnswerIncomingCall: (incomingId: string) => Promise<void>;
  onDeclineIncomingCall: (incomingId: string) => Promise<void>;
  onHoldIncomingCall: (incomingId: string) => Promise<void>;
  onDismissCallTakenNotice: (noticeId: string) => void;
  resolutionModalOpen: boolean;
  followUpModalOpen: boolean;
  pendingResolution: ResolveCallSubmission | null;
  resolutionContext: CallResolutionContext | null;
  resolutionError: string | null;
  buildFollowUpTitle: (resolution: ResolveCallSubmission | null) => string;
  onDeferResolution: () => void;
  onDeferResolutionAndNavigateDashboard: () => void;
  onSubmitResolution: (resolution: ResolveCallSubmission) => Promise<void>;
  onSubmitFollowUpAndFinalize: (payload: CreateTaskSubmission) => Promise<void>;
};

function cn(...classes: Array<string | undefined | false | null>) {
  return classes.filter(Boolean).join(" ");
}

export function CallUiShell({
  audioRef,
  pendingInvite,
  callDirection,
  callStatus,
  isOnDashboardRoute,
  incomingToastCalls,
  callTakenNotices,
  answerLockoutActive = false,
  answerLockoutMessage,
  onDismissInvite,
  onJoinConferenceRoom,
  onAnswerIncomingCall,
  onDeclineIncomingCall,
  onHoldIncomingCall,
  onDismissCallTakenNotice,
  resolutionModalOpen,
  followUpModalOpen,
  pendingResolution,
  resolutionContext,
  resolutionError,
  buildFollowUpTitle,
  onDeferResolution,
  onDeferResolutionAndNavigateDashboard,
  onSubmitResolution,
  onSubmitFollowUpAndFinalize,
}: CallUiShellProps) {
  const shouldShowIncomingToasts =
    !isOnDashboardRoute && callDirection === "incoming" && callStatus === "ringing";
  const visibleIncomingToasts = shouldShowIncomingToasts ? incomingToastCalls : [];
  const shouldPlayIncomingRingtone =
    callDirection === "incoming" && callStatus === "ringing";
  const shouldPlayOutboundRingback =
    callDirection === "outgoing" && callStatus === "dialing";
  const ringtoneAudioRef = React.useRef<HTMLAudioElement | null>(null);
  const ringtoneStartRef = React.useRef<(() => void) | null>(null);
  const ringbackIntervalRef = React.useRef<number | null>(null);
  const ringbackContextRef = React.useRef<AudioContext | null>(null);
  const ringbackStartRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    const stopRingtone = () => {
      const audio = ringtoneAudioRef.current;
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
      ringtoneAudioRef.current = null;
    };

    if (!shouldPlayIncomingRingtone) {
      stopRingtone();
      ringtoneStartRef.current = null;
      return;
    }

    if (typeof window === "undefined") return;

    let cancelled = false;
    const audio = new Audio("/audio/incoming-ringtone.mp3");
    audio.loop = true;
    audio.preload = "auto";
    ringtoneAudioRef.current = audio;

    const startRingtone = () => {
      if (cancelled || ringtoneAudioRef.current !== audio) return;
      void audio.play().catch(() => {});
    };
    ringtoneStartRef.current = startRingtone;

    startRingtone();
    const retryStart = () => {
      ringtoneStartRef.current?.();
    };
    window.addEventListener("pointerdown", retryStart, { once: true });
    window.addEventListener("keydown", retryStart, { once: true });

    return () => {
      window.removeEventListener("pointerdown", retryStart);
      window.removeEventListener("keydown", retryStart);
      if (cancelled) return;
      cancelled = true;
      stopRingtone();
      ringtoneStartRef.current = null;
    };
  }, [shouldPlayIncomingRingtone]);

  React.useEffect(() => {
    const stopRingback = () => {
      if (ringbackIntervalRef.current !== null) {
        window.clearInterval(ringbackIntervalRef.current);
        ringbackIntervalRef.current = null;
      }
      if (ringbackContextRef.current) {
        void ringbackContextRef.current.close().catch(() => {});
        ringbackContextRef.current = null;
      }
    };

    if (!shouldPlayOutboundRingback) {
      stopRingback();
      ringbackStartRef.current = null;
      return;
    }

    if (typeof window === "undefined") return;
    const AudioCtx =
      window.AudioContext ||
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioCtx) return;

    const context = new AudioCtx();
    ringbackContextRef.current = context;
    let cancelled = false;

    const beep = (frequency: number, durationMs: number, atSeconds: number) => {
      if (cancelled) return;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, atSeconds);
      gain.gain.exponentialRampToValueAtTime(0.07, atSeconds + 0.02);
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        atSeconds + durationMs / 1000
      );
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(atSeconds);
      oscillator.stop(atSeconds + durationMs / 1000 + 0.02);
    };

    const playPattern = () => {
      const now = context.currentTime + 0.01;
      for (let step = 0; step < 8; step += 1) {
        beep(step % 2 === 0 ? 440 : 480, 120, now + step * 0.14);
      }
    };

    const startRingback = () => {
      void context.resume().then(() => {
        if (cancelled) return;
        if (ringbackIntervalRef.current !== null) return;
        playPattern();
        ringbackIntervalRef.current = window.setInterval(playPattern, 4000);
      }).catch(() => {});
    };
    ringbackStartRef.current = startRingback;

    startRingback();
    const retryStart = () => {
      ringbackStartRef.current?.();
    };
    window.addEventListener("pointerdown", retryStart, { once: true });
    window.addEventListener("keydown", retryStart, { once: true });

    return () => {
      window.removeEventListener("pointerdown", retryStart);
      window.removeEventListener("keydown", retryStart);
      if (cancelled) return;
      cancelled = true;
      stopRingback();
      ringbackStartRef.current = null;
    };
  }, [shouldPlayOutboundRingback]);

  return (
    <>
      <audio ref={audioRef} autoPlay playsInline className="hidden" />
      <ConferenceInviteModal
        invite={pendingInvite}
        onDismiss={onDismissInvite}
        onJoin={onJoinConferenceRoom}
      />
      <div className="pointer-events-none fixed right-6 top-18 z-70 flex w-90 max-w-[calc(100vw-24px)] flex-col gap-3">
        {callTakenNotices.map((notice) => (
          <div
            key={notice.id}
            className={cn(
              "pointer-events-auto rounded-[14px] border border-[#2C313B] bg-[#141B27] p-4",
              "shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#99A1AA]">
                  Call answered elsewhere
                </div>
                <div className="mt-1 text-[14px] font-semibold text-white">
                  {notice.caller ?? "Incoming call"} was taken by{" "}
                  {notice.takenByName}
                </div>
                <div className="mt-1 text-[12px] text-[#727C92]">
                  Ext {notice.takenByExtension}
                </div>
              </div>
              <button
                type="button"
                data-testid="call-taken-notice-dismiss"
                onClick={() => onDismissCallTakenNotice(notice.id)}
                className="pointer-events-auto rounded-[8px] px-2 py-1 text-[12px] font-semibold text-[#99A1AA] hover:bg-white/5"
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}
        {visibleIncomingToasts.map((incomingCall, index) => (
          <CallNotificationToast
            key={incomingCall.id}
            incomingCall={incomingCall}
            stackOffset={index}
            onAnswer={onAnswerIncomingCall}
            onDecline={onDeclineIncomingCall}
            onHold={onHoldIncomingCall}
            answerDisabled={answerLockoutActive}
            answerDisabledMessage={answerLockoutMessage}
          />
        ))}
      </div>

      <ResolveCallModal
        isOpen={resolutionModalOpen}
        onClose={onDeferResolution}
        onDeferAndNavigateDashboard={onDeferResolutionAndNavigateDashboard}
        onSubmit={onSubmitResolution}
        submitError={resolutionError}
      />

      <CreateTaskModal
        isOpen={followUpModalOpen}
        onClose={onDeferResolution}
        onDeferAndNavigateDashboard={onDeferResolutionAndNavigateDashboard}
        onSubmit={onSubmitFollowUpAndFinalize}
        customerName={
          resolutionContext?.customerName ?? null
        }
        callerNumber={
          resolutionContext?.callerNumber ?? null
        }
        defaultTitle={buildFollowUpTitle(pendingResolution)}
      />
    </>
  );
}
